import React, { useEffect, useMemo, useRef, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';

import Input from '../../../components/ui/Input';
import Select from '../../../components/ui/Select';
import Button from '../../../components/ui/Button';
import Icon from '../../../components/appIcon';
import Badge from '../../../components/ui/Badge';

import { SECTIONS } from './workOrderConfig';
import { useWorkOrderForm } from './hooks/useWorkOrderForm';
import { usePartsManagement } from './hooks/usePartsManagement';
import { useResponsiveModalLayout } from './hooks/useResponsiveModalLayout';
import { useVehicleHistoryAutoFill } from './hooks/useVehicleHistoryAutoFill';
import { useWorkOrderSubmission } from './hooks/useWorkOrderSubmission';

const NewWorkOrderModal = ({
    isOpen,
    onClose,
    onCreate,
    userRole,
    currentUser,
    vehicleHistory = [],
    inventory = [],
    initialData = null
}) => {
    // Form
    const { form, updateForm, resetForm } = useWorkOrderForm(initialData || {});

    // Parts
    const {
        selectedParts,
        manualParts,
        setSelectedParts,
        setManualParts,
        newManualPart,
        setNewManualPart,
        handlePartSelection,
        removePart,
        removeManualPart,
        updatePartQuantity,
        updateManualPartQuantity,
        addManualPart,
        updateNewManualPart,
        resetParts
    } = usePartsManagement();

    // ✅ Layout (altura móvil) + helper cerrar teclado
    const { isMobile, windowHeight, dismissKeyboard } = useResponsiveModalLayout();

    // ✅ Fallback por si tu hook aún no trae dismissKeyboard
    const blurActive = () => {
        if (typeof document === 'undefined') return;
        const el = document.activeElement;
        if (el && typeof el.blur === 'function') el.blur();
    };
    const closeKeyboard = () => {
        if (dismissKeyboard) dismissKeyboard();
        else blurActive();
    };

    // Prefill edit mode
    useEffect(() => {
        if (!isOpen || !initialData) return;

        try {
            updateForm({ ...initialData });
            setSelectedParts(Array.isArray(initialData.partsUsed) ? initialData.partsUsed : []);
            setManualParts(Array.isArray(initialData.manualParts) ? initialData.manualParts : []);
            setNewManualPart({ name: '', quantity: 1 });
        } catch (e) {
            console.error('[NewWorkOrderModal] preload initialData error:', e);
        }
    }, [isOpen, initialData, updateForm, setSelectedParts, setManualParts, setNewManualPart]);

    // Reset only on new
    useEffect(() => {
        if (!isOpen) return;
        if (initialData) return;

        resetForm();
        resetParts();
        setNewManualPart({ name: '', quantity: 1 });
    }, [isOpen, initialData, resetForm, resetParts, setNewManualPart]);

    // Vehicle info
    const vehicleInfo = useVehicleHistoryAutoFill(form, vehicleHistory, updateForm);

    // Scroll system
    const scrollRef = useRef(null);

    const sectionRefs = useMemo(() => {
        const refs = {};
        SECTIONS.forEach((s) => {
            refs[s.id] = React.createRef();
        });
        return refs;
    }, []);

    const [activeSection, setActiveSection] = useState(SECTIONS?.[0]?.id || 'vehicle');
    const [progress, setProgress] = useState(0);

    const scrollToSection = (id) => {
        closeKeyboard(); // ✅ cierra teclado al cambiar sección
        const el = sectionRefs[id]?.current;
        if (!el) return;
        el.scrollIntoView({ behavior: 'smooth', block: 'start' });
    };

    // Progress by scroll
    useEffect(() => {
        const el = scrollRef.current;
        if (!el) return;

        const onScroll = () => {
            const max = el.scrollHeight - el.clientHeight;
            const pct = max > 0 ? Math.round((el.scrollTop / max) * 100) : 0;
            setProgress(Math.max(0, Math.min(100, pct)));
        };

        onScroll();
        el.addEventListener('scroll', onScroll, { passive: true });
        return () => el.removeEventListener('scroll', onScroll);
    }, []);

    // ✅ Si el usuario empieza a scrollear en mobile, cierra teclado
    useEffect(() => {
        const el = scrollRef.current;
        if (!el) return;

        const onTouchMove = () => closeKeyboard();
        el.addEventListener('touchmove', onTouchMove, { passive: true });
        return () => el.removeEventListener('touchmove', onTouchMove);
    }, [dismissKeyboard]);

    // Scrollspy
    useEffect(() => {
        const root = scrollRef.current;
        if (!root) return;

        const obs = new IntersectionObserver(
            (entries) => {
                const best = entries
                    .filter((e) => e.isIntersecting)
                    .sort((a, b) => (b.intersectionRatio || 0) - (a.intersectionRatio || 0))[0];

                if (best?.target?.dataset?.sectionId) {
                    setActiveSection(best.target.dataset.sectionId);
                }
            },
            {
                root,
                threshold: [0.15, 0.3, 0.6],
                rootMargin: '-10% 0px -70% 0px'
            }
        );

        SECTIONS.forEach((s) => {
            const el = sectionRefs[s.id]?.current;
            if (el) obs.observe(el);
        });

        return () => obs.disconnect();
    }, [sectionRefs]);

    // Submission
    const { isSubmitting, isGeneratingPDF, handleSubmit } = useWorkOrderSubmission({
        form,
        selectedParts,
        manualParts,
        userRole,
        currentUser,
        onCreate,
        onClose,
        resetForm,
        resetParts,
        setActiveSection: () => { }
    });

    if (!isOpen) return null;

    const totalPartsCount = (selectedParts?.length || 0) + (manualParts?.length || 0);
    const folio = form?.workOrderNumber || initialData?.workOrderNumber || 'NUEVO-REPORTE';

    return (
        <AnimatePresence>
            <div className="fixed inset-0 z-50 p-0 safe-area-inset overflow-hidden">
                {/* ✅ Estilos SOLO para este modal (sin jsx/global para evitar warnings) */}
                <style>{`
          .workorder-modal .no-scrollbar::-webkit-scrollbar { display: none; }
          .workorder-modal .no-scrollbar { -ms-overflow-style: none; scrollbar-width: none; }

          /* ✅ Anti-zoom iOS “por si acaso” (si algún input/textarea trae font-size < 16px) */
          .workorder-modal input,
          .workorder-modal select,
          .workorder-modal textarea {
            font-size: 16px;
          }
        `}</style>

                {/* Overlay */}
                <motion.div
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    exit={{ opacity: 0 }}
                    className="absolute inset-0 bg-gradient-to-b from-gray-700/60 via-gray-800/55 to-gray-900/60 backdrop-blur-lg"
                    onClick={() => {
                        closeKeyboard();
                        onClose();
                    }}
                />

                {/* Contenedor principal FULL SCREEN */}
                <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: 20 }}
                    transition={{ type: 'spring', damping: 24, stiffness: 260 }}
                    className="workorder-modal relative z-10 w-full h-full bg-gray-950/98 border border-gray-900/80 shadow-[0_0_0_1px_rgba(15,23,42,0.9),0_40px_120px_rgba(0,0,0,0.95)] flex flex-col overflow-hidden"
                    style={isMobile ? { height: `${windowHeight}px` } : { height: '100vh' }}
                >
                    {/* Header */}
                    <div className="flex items-center justify-between px-4 lg:px-6 py-4 border-b border-gray-800 bg-gradient-to-r from-gray-950 via-gray-900 to-gray-900 pt-14 lg:pt-6">
                        <div className="flex items-center gap-3 min-w-0">
                            <div className="w-11 h-11 rounded-2xl bg-gradient-to-br from-cyan-500 via-blue-500 to-indigo-500 flex items-center justify-center shadow-lg shadow-cyan-500/40">
                                <Icon name="FileText" className="w-5 h-5 text-white" />
                            </div>
                            <div className="min-w-0">
                                <h2 className="text-base sm:text-lg lg:text-2xl font-bold text-white flex items-center gap-2 truncate">
                                    Reporte de trabajo
                                    <span className="text-[11px] font-mono px-2 py-0.5 rounded-full bg-cyan-500/15 text-cyan-300 border border-cyan-500/40 flex-shrink-0">
                                        FOLIO: {folio}
                                    </span>
                                </h2>
                                <p className="text-[11px] sm:text-xs lg:text-sm text-gray-400 flex items-center gap-2">
                                    <Icon name="Navigation" className="w-3 h-3 sm:w-4 sm:h-4 text-cyan-300 flex-shrink-0" />
                                    <span className="truncate">{SECTIONS.find((s) => s.id === activeSection)?.label}</span>
                                </p>
                            </div>
                        </div>

                        <div className="flex items-start sm:items-center gap-3 flex-shrink-0">
                            <div className="hidden sm:flex flex-col items-end text-[11px] text-gray-400">
                                <span>
                                    Cliente: <span className="text-gray-200 font-medium">{form.client || 'Sin definir'}</span>
                                </span>
                                <span>
                                    Refacciones: <span className="text-cyan-300 font-semibold">{totalPartsCount}</span>
                                </span>
                            </div>

                            <button
                                onClick={() => {
                                    closeKeyboard();
                                    onClose();
                                }}
                                className="p-2 lg:p-3 hover:bg-gray-900 rounded-xl transition-colors group ml-2 flex-shrink-0 active:scale-95"
                            >
                                <Icon name="X" size={20} className="lg:w-6 lg:h-6 text-gray-400 group-hover:text-white" />
                            </button>
                        </div>
                    </div>

                    {/* NAV SUPERIOR */}
                    <div className="px-3 sm:px-4 lg:px-6 py-3 border-b border-gray-900/80 bg-gray-950/90">
                        <div className="flex gap-2 overflow-x-auto no-scrollbar">
                            {SECTIONS.map((section) => (
                                <button
                                    key={section.id}
                                    onClick={() => scrollToSection(section.id)}
                                    className={`flex items-center gap-2 px-3 py-2 rounded-full border text-xs sm:text-sm whitespace-nowrap transition active:scale-95 ${activeSection === section.id
                                            ? 'bg-cyan-500/10 border-cyan-500/40 text-cyan-200'
                                            : 'bg-gray-900/40 border-gray-800 text-gray-300 hover:bg-gray-900'
                                        }`}
                                >
                                    <Icon
                                        name={section.icon}
                                        size={14}
                                        className={activeSection === section.id ? 'text-cyan-300' : 'text-gray-400'}
                                    />
                                    {section.label}
                                </button>
                            ))}
                        </div>
                    </div>

                    {/* Progreso */}
                    <div className="px-4 lg:px-6 pt-3 lg:pt-4 bg-gray-950/90 border-b border-gray-900/80">
                        <div className="flex items-center justify-between mb-1 lg:mb-2">
                            <span className="text-xs lg:text-sm text-cyan-300 font-medium">Progreso</span>
                            <span className="text-xs lg:text-sm font-medium text-cyan-300">{progress}%</span>
                        </div>
                        <div className="w-full bg-gray-900 rounded-full h-1.5 lg:h-2 mb-3 border border-gray-800/80 overflow-hidden">
                            <motion.div
                                initial={{ width: 0 }}
                                animate={{ width: `${progress}%` }}
                                transition={{ duration: 0.3, ease: 'easeOut' }}
                                className="bg-gradient-to-r from-cyan-500 via-blue-500 to-indigo-500 h-1.5 lg:h-2 rounded-full shadow-[0_0_18px_rgba(56,189,248,0.4)]"
                            />
                        </div>
                    </div>

                    {/* BODY SCROLL */}
                    <div
                        ref={scrollRef}
                        className="flex-1 overflow-y-auto overflow-x-hidden p-3 sm:p-4 lg:p-8 safe-area-inset-bottom space-y-8 lg:space-y-10"
                    >
                        {/* ========= VEHICLE ========= */}
                        <section ref={sectionRefs.vehicle} data-section-id="vehicle" className="scroll-mt-28">
                            <div className="bg-gray-900/80 border border-gray-800/90 rounded-2xl p-4 sm:p-5 shadow-inner shadow-black/40 space-y-3">
                                <div className="flex items-center justify-between gap-3 mb-1">
                                    <div className="flex items-center gap-2">
                                        <div className="w-8 h-8 rounded-xl bg-cyan-600/20 flex items-center justify-center text-cyan-300 border border-cyan-500/40">
                                            <Icon name="User" className="w-4 h-4" />
                                        </div>
                                        <div>
                                            <h3 className="text-sm sm:text-base font-semibold text-white">Datos del servicio</h3>
                                            <p className="text-[11px] text-gray-400">Información que aparecerá en la cabecera del PDF.</p>
                                        </div>
                                    </div>
                                </div>

                                <div className="grid gap-3 md:grid-cols-3">
                                    <Input
                                        label="Cliente"
                                        value={form.client}
                                        onChange={(e) => updateForm({ client: e.target.value })}
                                        placeholder="Nombre del cliente"
                                        className="bg-gray-950/40 border-gray-800 text-sm"
                                    />
                                    <Input
                                        label="Unidad"
                                        value={form.unit}
                                        onChange={(e) => updateForm({ unit: e.target.value })}
                                        placeholder="Número de unidad"
                                        className="bg-gray-950/40 border-gray-800 text-sm"
                                    />
                                    <Input
                                        label="Motor"
                                        value={form.engine}
                                        onChange={(e) => updateForm({ engine: e.target.value })}
                                        placeholder="Tipo de motor"
                                        className="bg-gray-950/40 border-gray-800 text-sm"
                                    />
                                    <Input
                                        label="Modelo"
                                        value={form.model}
                                        onChange={(e) => updateForm({ model: e.target.value })}
                                        placeholder="Modelo del vehículo"
                                        className="bg-gray-950/40 border-gray-800 text-sm"
                                    />
                                    <Input
                                        label="VIN"
                                        value={form.vin}
                                        onChange={(e) => updateForm({ vin: e.target.value.toUpperCase() })}
                                        placeholder="(Opcional)"
                                        maxLength={17}
                                        className="bg-gray-950/40 border-gray-800 text-sm"
                                    />
                                    <Input
                                        label="Placas"
                                        value={form.plates}
                                        onChange={(e) => updateForm({ plates: e.target.value.toUpperCase() })}
                                        placeholder="ABC1234"
                                        className="bg-gray-950/40 border-gray-800 text-sm"
                                    />
                                    <Input
                                        label="Kilometraje actual"
                                        type="number"
                                        value={form.mileage}
                                        onChange={(e) => updateForm({ mileage: e.target.value })}
                                        placeholder="150000"
                                        className="bg-gray-950/40 border-gray-800 text-sm"
                                    />
                                    <Input
                                        label="Fecha de ingreso"
                                        type="date"
                                        value={form.date}
                                        onChange={(e) => updateForm({ date: e.target.value })}
                                        className="bg-gray-950/40 border-gray-800 text-sm"
                                    />
                                    <Input
                                        label="Folio"
                                        value={form.workOrderNumber}
                                        onChange={(e) => updateForm({ workOrderNumber: e.target.value.toUpperCase() })}
                                        placeholder="WO-2024-001"
                                        className="bg-gray-950/40 border-gray-800 text-sm"
                                    />
                                </div>
                            </div>

                            {vehicleInfo && (
                                <motion.div
                                    initial={{ opacity: 0, y: 10 }}
                                    animate={{ opacity: 1, y: 0 }}
                                    className="bg-cyan-950/60 border border-cyan-500/50 rounded-2xl p-4 lg:p-5 shadow-[0_20px_60px_rgba(8,47,73,0.8)] mt-5"
                                >
                                    <div className="flex items-center gap-3 mb-3 lg:mb-4">
                                        <div className="w-8 h-8 lg:w-10 lg:h-10 bg-cyan-600 rounded-xl flex items-center justify-center shadow-lg shadow-cyan-500/40">
                                            <Icon name="Database" size={16} className="lg:w-5 lg:h-5 text-white" />
                                        </div>
                                        <div className="min-w-0">
                                            <h4 className="font-bold text-cyan-200 text-sm sm:text-base lg:text-lg truncate">Historial encontrado</h4>
                                            <p className="text-cyan-300 text-xs lg:text-sm truncate">Datos del sistema de registros</p>
                                        </div>
                                    </div>
                                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 lg:gap-4">
                                        <div className="bg-gray-950/60 p-3 rounded-xl border border-cyan-500/30">
                                            <p className="text-cyan-300 text-xs lg:text-sm font-medium">Último servicio</p>
                                            <p className="text-white font-semibold text-xs sm:text-sm lg:text-base truncate">
                                                {vehicleInfo.lastService || 'No registrado'}
                                            </p>
                                        </div>
                                        <div className="bg-gray-950/60 p-3 rounded-xl border border-cyan-500/30">
                                            <p className="text-cyan-300 text-xs lg:text-sm font-medium">Fallas anteriores</p>
                                            <p className="text-white font-semibold text-xs sm:text-sm lg:text-base truncate">
                                                {vehicleInfo.previousIssues?.join(', ') || 'Ninguna'}
                                            </p>
                                        </div>
                                    </div>
                                </motion.div>
                            )}
                        </section>

                        {/* DRIVER */}
                        <section ref={sectionRefs.driver} data-section-id="driver" className="scroll-mt-28">
                            <div className="bg-gray-900/85 rounded-2xl p-4 lg:p-5 border border-gray-800">
                                <h3 className="text-sm sm:text-base lg:text-lg font-bold text-white mb-3 lg:mb-4 flex items-center gap-3">
                                    <div className="w-8 h-8 lg:w-10 lg:h-10 bg-blue-600 rounded-xl flex items-center justify-center shadow-lg shadow-blue-500/40">
                                        <Icon name="User" size={16} className="lg:w-5 lg:h-5 text-white" />
                                    </div>
                                    Información del chofer
                                </h3>
                                <Input
                                    label="Nombre o apodo del chofer"
                                    value={form.driverName}
                                    onChange={(e) => updateForm({ driverName: e.target.value })}
                                    placeholder="Nombre completo del operador"
                                    className="bg-gray-950/50 border-gray-800 focus:border-cyan-400 text-white font-semibold text-base"
                                />
                            </div>

                            <div className="bg-gray-900/85 rounded-2xl p-4 lg:p-5 border border-gray-800 mt-4">
                                <h3 className="text-sm sm:text-base lg:text-lg font-bold text-white mb-3 lg:mb-4 flex items-center gap-3">
                                    <div className="w-8 h-8 lg:w-10 lg:h-10 bg-orange-600 rounded-xl flex items-center justify-center shadow-lg shadow-orange-500/40">
                                        <Icon name="AlertTriangle" size={16} className="lg:w-5 lg:h-5 text-white" />
                                    </div>
                                    Reporte del chofer
                                </h3>
                                <label className="block text-xs sm:text-sm font-medium text-cyan-300 mb-2 lg:mb-3">
                                    Descripción de la falla
                                </label>
                                <textarea
                                    className="w-full h-28 sm:h-32 lg:h-40 p-3 lg:p-4 bg-gray-950/50 border border-gray-800 rounded-xl focus:ring-2 focus:ring-cyan-400 focus:border-cyan-400 resize-none text-white placeholder-gray-400 font-medium text-base"
                                    value={form.driverDescription}
                                    onChange={(e) => updateForm({ driverDescription: e.target.value })}
                                    placeholder="Describa detalladamente los síntomas..."
                                    maxLength={500}
                                />
                                <p className="text-gray-400 text-[11px] sm:text-xs lg:text-sm mt-2">
                                    {form.driverDescription?.length || 0}/500 caracteres
                                </p>
                            </div>
                        </section>

                        {/* TECHNICAL */}
                        <section ref={sectionRefs.technical} data-section-id="technical" className="scroll-mt-28">
                            <div className="bg-gradient-to-r from-gray-900 via-slate-900 to-gray-900 rounded-2xl p-4 lg:p-6 text-white shadow-[0_20px_60px_rgba(15,23,42,0.85)] border border-cyan-500/30">
                                <div className="flex items-center gap-3 lg:gap-4">
                                    <div className="w-10 h-10 lg:w-12 lg:h-12 bg-cyan-500/20 rounded-xl flex items-center justify-center backdrop-blur-sm border border-cyan-400/60">
                                        <Icon name="FileText" size={20} className="lg:w-6 lg:h-6 text-white" />
                                    </div>
                                    <div className="min-w-0">
                                        <h3 className="text-base sm:text-lg lg:text-2xl font-bold truncate">Descripción técnica profesional</h3>
                                        <p className="text-cyan-100 mt-1 text-xs sm:text-sm lg:text-base truncate">
                                            Documenta la falla con terminología técnica
                                        </p>
                                    </div>
                                </div>
                            </div>

                            <div className="grid grid-cols-1 gap-4 lg:gap-8 mt-4">
                                <div className="bg-gray-900/85 rounded-2xl p-4 lg:p-5 border border-gray-800">
                                    <h3 className="text-sm sm:text-base lg:text-lg font-bold text-white mb-3 lg:mb-4 flex items-center gap-3">
                                        <div className="w-8 h-8 lg:w-10 lg:h-10 bg-cyan-600 rounded-xl flex items-center justify-center shadow-lg shadow-cyan-500/40">
                                            <Icon name="Search" size={16} className="lg:w-5 lg:h-5 text-white" />
                                        </div>
                                        Tipo de diagnóstico
                                    </h3>

                                    <Select
                                        options={[
                                            { value: 'Diagnóstico eléctrico', label: '⚡ Diagnóstico eléctrico' },
                                            { value: 'Diagnóstico electrónico', label: '🔌 Diagnóstico electrónico' },
                                            { value: 'Diagnóstico mecánico', label: '🔧 Diagnóstico mecánico' },
                                            { value: 'Diagnóstico completo', label: '🔍 Diagnóstico completo' }
                                        ]}
                                        value={form.diagnosticType}
                                        onChange={(v) => {
                                            closeKeyboard(); // ✅ al cambiar opción, cierra teclado
                                            updateForm({ diagnosticType: v });
                                        }}
                                        className="bg-gray-950/50 border-gray-800 text-white font-semibold text-base"
                                    />
                                </div>

                                <div className="bg-gray-900/85 rounded-2xl p-4 lg:p-6 border border-gray-800">
                                    <label className="block text-base sm:text-lg lg:text-xl font-bold text-cyan-300 truncate">
                                        Análisis técnico detallado
                                    </label>

                                    <textarea
                                        className="w-full min-h-[180px] sm:min-h-[220px] lg:min-h-[300px] p-3 sm:p-4 bg-gray-950/50 border border-gray-800 rounded-xl focus:ring-2 focus:ring-cyan-400 focus:border-cyan-400 resize-none font-mono text-base text-white placeholder-gray-400 leading-relaxed font-medium mt-3"
                                        value={form.technicalDescription}
                                        onChange={(e) => updateForm({ technicalDescription: e.target.value })}
                                        maxLength={2000}
                                        placeholder="Describe diagnóstico, pruebas, códigos, etc..."
                                    />

                                    <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-1 sm:gap-2 text-[11px] sm:text-xs lg:text-sm mt-2">
                                        <span className="font-medium text-emerald-400">
                                            {(form.technicalDescription?.length || 0)} caracteres
                                        </span>
                                        <span className="text-gray-400">
                                            Restantes: {Math.max(0, 2000 - (form.technicalDescription?.length || 0))}
                                        </span>
                                    </div>
                                </div>
                            </div>
                        </section>

                        {/* OBSERVATIONS */}
                        <section ref={sectionRefs.observations} data-section-id="observations" className="scroll-mt-28">
                            <div className="bg-gradient-to-r from-gray-900 via-slate-900 to-gray-900 rounded-2xl p-4 lg:p-6 text-white shadow-[0_20px_60px_rgba(15,23,42,0.85)] border border-emerald-500/30">
                                <div className="flex items-center gap-3 lg:gap-4">
                                    <div className="w-10 h-10 lg:w-12 lg:h-12 bg-emerald-500/20 rounded-xl flex items-center justify-center backdrop-blur-sm border border-emerald-400/60">
                                        <Icon name="FileText" size={20} className="lg:w-6 lg:h-6 text-white" />
                                    </div>
                                    <div className="min-w-0">
                                        <h3 className="text-base sm:text-lg lg:text-2xl font-bold truncate">Observaciones adicionales</h3>
                                        <p className="text-emerald-100 mt-1 text-xs sm:text-sm lg:text-base truncate">
                                            Agrega información adicional relevante
                                        </p>
                                    </div>
                                </div>
                            </div>

                            <div className="bg-gray-900/85 rounded-2xl p-4 lg:p-6 border border-gray-800 mt-4">
                                <label className="block text-base sm:text-lg lg:text-xl font-bold text-cyan-300">
                                    Notas y comentarios adicionales
                                </label>
                                <textarea
                                    className="w-full min-h-[200px] sm:min-h-[260px] lg:min-h-[400px] p-3 sm:p-4 bg-gray-950/50 border border-gray-800 rounded-xl focus:ring-2 focus:ring-cyan-400 focus:border-cyan-400 resize-none text-base text-white placeholder-gray-400 leading-relaxed font-medium mt-3"
                                    value={form.additionalObservations}
                                    onChange={(e) => updateForm({ additionalObservations: e.target.value })}
                                    maxLength={2000}
                                    placeholder="Notas adicionales..."
                                />
                                <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-1 sm:gap-2 text-[11px] sm:text-xs lg:text-sm mt-2">
                                    <span className="font-medium text-cyan-300">
                                        {(form.additionalObservations?.length || 0)} caracteres
                                    </span>
                                    <span className="text-gray-400">
                                        Restantes: {Math.max(0, 2000 - (form.additionalObservations?.length || 0))}
                                    </span>
                                </div>
                            </div>
                        </section>

                        {/* PARTS */}
                        <section ref={sectionRefs.parts} data-section-id="parts" className="scroll-mt-28">
                            <div className="flex flex-col lg:flex-row lg:justify-between lg:items-center gap-3 lg:gap-0">
                                <div className="min-w-0">
                                    <h3 className="text-lg sm:text-xl lg:text-2xl font-bold text-white truncate">Gestión de refacciones</h3>
                                    <p className="text-cyan-300 mt-1 text-xs sm:text-sm lg:text-base truncate">
                                        Selecciona las refacciones necesarias
                                    </p>
                                </div>
                                <Badge className="bg-cyan-500/15 text-cyan-200 border-cyan-500/50 text-[11px] sm:text-xs lg:text-sm whitespace-nowrap rounded-full px-3 py-1">
                                    {totalPartsCount} refacciones
                                </Badge>
                            </div>

                            <div className="grid grid-cols-1 gap-4 lg:gap-8 xl:grid-cols-2 mt-4">
                                {/* Inventario */}
                                <div className="space-y-4">
                                    <div className="bg-gray-900/85 rounded-2xl p-4 lg:p-6 border border-gray-800">
                                        <h4 className="font-bold text-white text-sm sm:text-base lg:text-lg mb-3 lg:mb-4 flex items-center gap-3">
                                            <div className="w-8 h-8 lg:w-10 lg:h-10 bg-blue-600 rounded-xl flex items-center justify-center shadow-lg shadow-blue-500/40">
                                                <Icon name="Package" size={16} className="lg:w-5 lg:h-5 text-white" />
                                            </div>
                                            Inventario disponible
                                        </h4>

                                        {inventory.length === 0 ? (
                                            <div className="text-center py-6 text-gray-500 text-sm">
                                                No hay refacciones registradas en el inventario.
                                            </div>
                                        ) : (
                                            <div className="max-h-56 sm:max-h-60 lg:max-h-80 overflow-y-auto overflow-x-hidden space-y-2 lg:space-y-3 pr-1">
                                                {inventory.map((part) => (
                                                    <motion.div
                                                        key={part.id}
                                                        whileHover={{ scale: 1.02 }}
                                                        className={`p-3 lg:p-4 border rounded-xl cursor-pointer transition-all ${part.stock > 0
                                                                ? 'border-gray-700 hover:border-cyan-400 hover:bg-cyan-900/15'
                                                                : 'opacity-50 bg-gray-900 border-gray-700'
                                                            }`}
                                                        onClick={() => {
                                                            closeKeyboard(); // ✅ al seleccionar opción, cierra teclado
                                                            if (part.stock > 0) handlePartSelection(part);
                                                        }}
                                                    >
                                                        <div className="flex justify-between items-start gap-2">
                                                            <div className="flex-1 min-w-0">
                                                                <p className="font-semibold text-white text-xs sm:text-sm lg:text-base truncate">{part.name}</p>
                                                                <p className="text-[11px] sm:text-xs lg:text-sm text-gray-400 font-mono truncate">{part.id}</p>
                                                                <p className="text-[10px] sm:text-xs text-gray-500 mt-1 truncate">{part.category}</p>
                                                            </div>
                                                            <div className="text-right flex-shrink-0 ml-2">
                                                                <p className="text-[11px] sm:text-xs font-medium text-cyan-300">Stock: {part.stock}</p>
                                                            </div>
                                                        </div>
                                                    </motion.div>
                                                ))}
                                            </div>
                                        )}
                                    </div>

                                    {/* Agregar manual */}
                                    <div className="bg-gray-900/85 rounded-2xl p-4 lg:p-6 border border-gray-800">
                                        <h4 className="font-bold text-white text-sm sm:text-base lg:text-lg mb-3 lg:mb-4 flex items-center gap-3">
                                            <div className="w-8 h-8 lg:w-10 lg:h-10 bg-emerald-600 rounded-xl flex items-center justify-center shadow-lg shadow-emerald-500/40">
                                                <Icon name="Plus" size={16} className="lg:w-5 lg:h-5 text-white" />
                                            </div>
                                            Agregar refacción manual
                                        </h4>

                                        <div className="space-y-3 lg:space-y-4">
                                            <div className="grid grid-cols-1 gap-2 lg:gap-3">
                                                <Input
                                                    label="Nombre de la refacción"
                                                    value={newManualPart.name}
                                                    onChange={(e) => updateNewManualPart({ name: e.target.value })}
                                                    placeholder="Ej: Filtro de aceite"
                                                    className="bg-gray-950/50 border-gray-800 focus:border-cyan-400 text-white font-semibold text-base"
                                                />
                                                <Input
                                                    label="Cantidad"
                                                    type="number"
                                                    value={newManualPart.quantity}
                                                    onChange={(e) => {
                                                        const value = parseInt(e.target.value, 10);
                                                        updateNewManualPart({ quantity: Number.isNaN(value) || value < 1 ? 1 : value });
                                                    }}
                                                    min="1"
                                                    className="bg-gray-950/50 border-gray-800 focus:border-cyan-400 text-white font-semibold text-base"
                                                />
                                            </div>

                                            <Button
                                                onClick={() => {
                                                    closeKeyboard();
                                                    addManualPart();
                                                }}
                                                disabled={!newManualPart.name.trim()}
                                                className="w-full bg-gradient-to-r from-emerald-500 to-green-600 hover:from-emerald-600 hover:to-green-700 text-white py-3 text-sm sm:text-base shadow-lg shadow-emerald-500/40 disabled:opacity-40 disabled:cursor-not-allowed"
                                            >
                                                <Icon name="Plus" size={14} className="lg:w-4 lg:h-4 mr-2" />
                                                Agregar refacción manual
                                            </Button>
                                        </div>
                                    </div>
                                </div>

                                {/* Seleccionadas */}
                                <div className="space-y-4">
                                    <div className="bg-gray-900/85 rounded-2xl p-4 lg:p-6 border border-gray-800">
                                        <h4 className="font-bold text-white text-sm sm:text-base lg:text-lg mb-3 lg:mb-4 flex items-center gap-3">
                                            <div className="w-8 h-8 lg:w-10 lg:h-10 bg-emerald-600 rounded-xl flex items-center justify-center shadow-lg shadow-emerald-500/40">
                                                <Icon name="ShoppingCart" size={16} className="lg:w-5 lg:h-5 text-white" />
                                            </div>
                                            Refacciones en la orden
                                        </h4>

                                        {(selectedParts?.length || 0) === 0 && (manualParts?.length || 0) === 0 ? (
                                            <div className="text-center py-8 lg:py-12 text-gray-500">
                                                <Icon name="PackageX" size={32} className="lg:w-12 lg:h-12 mx-auto mb-2 lg:mb-3 opacity-50" />
                                                <p className="text-sm sm:text-base lg:text-lg">No hay refacciones seleccionadas</p>
                                                <p className="text-[11px] sm:text-xs lg:text-sm mt-1">Selecciona refacciones del inventario</p>
                                            </div>
                                        ) : (
                                            <div className="space-y-3 lg:space-y-4 max-h-56 sm:max-h-60 lg:max-h-80 overflow-y-auto overflow-x-hidden pr-1">
                                                {(selectedParts || []).map((part) => (
                                                    <motion.div
                                                        key={part.id}
                                                        initial={{ opacity: 0, y: 10 }}
                                                        animate={{ opacity: 1, y: 0 }}
                                                        className="p-3 lg:p-4 border border-cyan-500/60 bg-cyan-900/15 rounded-xl"
                                                    >
                                                        <div className="flex justify-between items-start mb-2 lg:mb-3 gap-2">
                                                            <div className="flex-1 min-w-0">
                                                                <p className="font-semibold text-white text-xs sm:text-sm lg:text-base truncate">{part.name}</p>
                                                                <p className="text-[11px] sm:text-xs lg:text-sm text-gray-300 font-mono truncate">{part.id}</p>
                                                                <Badge
                                                                    variant="outline"
                                                                    className="bg-blue-500/15 text-blue-200 border-blue-500/50 text-[10px] sm:text-xs mt-1"
                                                                >
                                                                    Del inventario
                                                                </Badge>
                                                            </div>

                                                            <button
                                                                onClick={() => {
                                                                    closeKeyboard();
                                                                    removePart(part.id);
                                                                }}
                                                                className="p-1 lg:p-2 hover:bg-red-600/20 rounded-lg transition-colors text-red-400 hover:text-red-300 flex-shrink-0 ml-2 active:scale-95"
                                                            >
                                                                <Icon name="Trash2" size={14} className="lg:w-4 lg:h-4" />
                                                            </button>
                                                        </div>

                                                        <div className="flex justify-between items-center">
                                                            <div className="flex items-center gap-2 lg:gap-3">
                                                                <label className="text-[11px] sm:text-xs lg:text-sm text-gray-300">Cantidad:</label>
                                                                <div className="flex items-center gap-1 lg:gap-2">
                                                                    <button
                                                                        onClick={() => updatePartQuantity(part.id, part.quantity - 1)}
                                                                        disabled={part.quantity <= 1}
                                                                        className="w-6 h-6 lg:w-6 lg:h-6 bg-gray-800 rounded flex items-center justify-center disabled:opacity-30 active:scale-95"
                                                                    >
                                                                        <Icon name="Minus" size={10} className="lg:w-3 lg:h-3 text-white" />
                                                                    </button>
                                                                    <span className="w-6 lg:w-8 text-center font-semibold text-white text-xs sm:text-sm lg:text-base">
                                                                        {part.quantity}
                                                                    </span>
                                                                    <button
                                                                        onClick={() => updatePartQuantity(part.id, part.quantity + 1)}
                                                                        disabled={part.quantity >= part.stock}
                                                                        className="w-6 h-6 lg:w-6 lg:h-6 bg-gray-800 rounded flex items-center justify-center disabled:opacity-30 active:scale-95"
                                                                    >
                                                                        <Icon name="Plus" size={10} className="lg:w-3 lg:h-3 text-white" />
                                                                    </button>
                                                                </div>
                                                            </div>
                                                        </div>
                                                    </motion.div>
                                                ))}

                                                {(manualParts || []).map((part, index) => (
                                                    <motion.div
                                                        key={part.id ?? `manual-${index}`}
                                                        initial={{ opacity: 0, y: 10 }}
                                                        animate={{ opacity: 1, y: 0 }}
                                                        className="p-3 lg:p-4 border border-emerald-500/60 bg-emerald-900/15 rounded-xl"
                                                    >
                                                        <div className="flex justify-between items-start mb-2 lg:mb-3 gap-2">
                                                            <div className="flex-1 min-w-0">
                                                                <p className="font-semibold text-white text-xs sm:text-sm lg:text-base truncate">{part.name}</p>
                                                                <Badge
                                                                    variant="outline"
                                                                    className="bg-emerald-500/15 text-emerald-200 border-emerald-500/50 text-[10px] sm:text-xs mt-1"
                                                                >
                                                                    Refacción manual
                                                                </Badge>
                                                            </div>

                                                            <button
                                                                onClick={() => {
                                                                    closeKeyboard();
                                                                    removeManualPart(index);
                                                                }}
                                                                className="p-1 lg:p-2 hover:bg-red-600/20 rounded-lg transition-colors text-red-400 hover:text-red-300 flex-shrink-0 ml-2 active:scale-95"
                                                            >
                                                                <Icon name="Trash2" size={14} className="lg:w-4 lg:h-4" />
                                                            </button>
                                                        </div>

                                                        <div className="flex justify-between items-center">
                                                            <div className="flex items-center gap-2 lg:gap-3">
                                                                <label className="text-[11px] sm:text-xs lg:text-sm text-gray-300">Cantidad:</label>
                                                                <div className="flex items-center gap-1 lg:gap-2">
                                                                    <button
                                                                        onClick={() => updateManualPartQuantity(index, part.quantity - 1)}
                                                                        disabled={part.quantity <= 1}
                                                                        className="w-6 h-6 lg:w-6 lg:h-6 bg-gray-800 rounded flex items-center justify-center disabled:opacity-30 active:scale-95"
                                                                    >
                                                                        <Icon name="Minus" size={10} className="lg:w-3 lg:h-3 text-white" />
                                                                    </button>

                                                                    <span className="w-6 lg:w-8 text-center font-semibold text-white text-xs sm:text-sm lg:text-base">
                                                                        {part.quantity}
                                                                    </span>

                                                                    <button
                                                                        onClick={() => updateManualPartQuantity(index, part.quantity + 1)}
                                                                        className="w-6 h-6 lg:w-6 lg:h-6 bg-gray-800 rounded flex items-center justify-center active:scale-95"
                                                                    >
                                                                        <Icon name="Plus" size={10} className="lg:w-3 lg:h-3 text-white" />
                                                                    </button>
                                                                </div>
                                                            </div>
                                                        </div>
                                                    </motion.div>
                                                ))}
                                            </div>
                                        )}
                                    </div>
                                </div>
                            </div>
                        </section>
                    </div>

                    {/* FOOTER */}
                    <div className="border-t border-gray-800 bg-gray-950/95 safe-area-inset-bottom px-3 sm:px-4 lg:px-6 py-3 lg:py-5">
                        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
                            <div className="flex flex-col sm:flex-row sm:items-center gap-2 text-[11px] sm:text-xs text-gray-400">
                                <span>
                                    Cliente: <span className="text-gray-200 font-medium">{form.client || 'Sin definir'}</span>
                                </span>
                                <span>
                                    Refacciones: <span className="text-cyan-300 font-semibold">{totalPartsCount}</span>
                                </span>
                                <span>
                                    Sección:{' '}
                                    <span className="text-cyan-200">{SECTIONS.find((s) => s.id === activeSection)?.label}</span>
                                </span>
                            </div>

                            <div className="flex flex-col sm:flex-row gap-2 lg:gap-3 w-full sm:w-auto">
                                <Button
                                    variant="outline"
                                    onClick={() => {
                                        closeKeyboard();
                                        onClose();
                                    }}
                                    className="w-full sm:w-auto border-gray-700 text-gray-300 hover:bg-gray-900 hover:text-white py-3 px-4 text-sm lg:text-sm min-h-[44px]"
                                >
                                    Cancelar
                                </Button>

                                <motion.div className="w-full sm:w-auto" whileHover={{ scale: 1.02 }} whileTap={{ scale: 0.98 }}>
                                    <Button
                                        onClick={() => {
                                            closeKeyboard();
                                            handleSubmit();
                                        }}
                                        disabled={isSubmitting || isGeneratingPDF}
                                        className="w-full sm:w-auto bg-gradient-to-r from-cyan-500 to-blue-600 hover:from-cyan-600 hover:to-blue-700 text-white shadow-xl shadow-cyan-500/40 py-3 px-6 text-sm lg:text-sm min-h-[44px] disabled:opacity-40 disabled:cursor-not-allowed"
                                    >
                                        {isSubmitting || isGeneratingPDF ? (
                                            <>
                                                <motion.div
                                                    animate={{ rotate: 360 }}
                                                    transition={{ duration: 1, repeat: Infinity, ease: 'linear' }}
                                                    className="mr-2"
                                                >
                                                    <Icon name="Loader" size={16} className="lg:w-5 lg:h-5" />
                                                </motion.div>
                                                {isGeneratingPDF ? 'Generando PDF...' : 'Creando orden...'}
                                            </>
                                        ) : (
                                            <>
                                                <Icon name="CheckCircle" size={16} className="lg:w-5 lg:h-5 mr-2" />
                                                Crear orden y PDF
                                            </>
                                        )}
                                    </Button>
                                </motion.div>
                            </div>
                        </div>
                    </div>
                </motion.div>
            </div>
        </AnimatePresence>
    );
};

export default NewWorkOrderModal;
